import React, { useEffect, useState } from 'react';
import axios from 'axios';

const stageLabel = (status) => ({
  PROPOSAL_STATUS_DEPOSIT_PERIOD: 'Deposit period',
  PROPOSAL_STATUS_VOTING_PERIOD: 'Voting period',
  PROPOSAL_STATUS_PASSED: 'Passed',
  PROPOSAL_STATUS_REJECTED: 'Rejected',
  PROPOSAL_STATUS_FAILED: 'Failed',
})[status] || 'Status unavailable';

const dateLabel = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
};

const voteOptions = [
  { key: 'yes', label: 'Yes' },
  { key: 'no', label: 'No' },
  { key: 'noWithVeto', label: 'No with veto' },
  { key: 'abstain', label: 'Abstain' },
];

const ProposalExplorer = ({ chainKey, chainName, initialId }) => {
  const [proposals, setProposals] = useState(null);
  const [listError, setListError] = useState('');
  const [selectedId, setSelectedId] = useState(initialId || null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');

  useEffect(() => {
    let active = true;
    setProposals(null);
    setListError('');
    axios.get(`/api/cosmos/chains/${encodeURIComponent(chainKey)}/proposals`)
      .then(({ data }) => { if (active) setProposals(data); })
      .catch(() => { if (active) setListError(`Proposals for ${chainName} are temporarily unavailable.`); });
    return () => { active = false; };
  }, [chainKey, chainName]);

  useEffect(() => {
    if (initialId) setSelectedId(initialId);
  }, [initialId]);

  useEffect(() => {
    if (!selectedId) return undefined;
    let active = true;
    setDetail(null);
    setDetailError('');
    axios.get(`/api/cosmos/chains/${encodeURIComponent(chainKey)}/proposals/${encodeURIComponent(selectedId)}`)
      .then(({ data }) => { if (active) setDetail(data); })
      .catch(() => { if (active) setDetailError('Proposal details are temporarily unavailable.'); });
    return () => { active = false; };
  }, [chainKey, selectedId]);

  useEffect(() => {
    if (!initialId || !proposals) return undefined;
    const frame = requestAnimationFrame(() => document.getElementById('cosmos-proposal-explorer')?.scrollIntoView({ block: 'start' }));
    return () => cancelAnimationFrame(frame);
  }, [initialId, proposals]);

  const selectProposal = (id) => setSelectedId((current) => current === id ? null : id);
  const tallyTotal = voteOptions.reduce((sum, option) => sum + Number(detail?.tally?.[option.key] || 0), 0);
  const voteAmount = (value) => {
    const amount = Number(value) / (10 ** Number(detail?.denomExponent || 0));
    return `${Number.isFinite(amount) ? amount.toLocaleString('en-GB', { maximumFractionDigits: 2 }) : '—'} ${detail?.symbol || ''}`;
  };
  const depositAmount = (coin) => coin.denom === detail?.stakingDenom
    ? `${(Number(coin.amount) / (10 ** Number(detail.denomExponent || 0))).toLocaleString('en-GB', { maximumFractionDigits: 2 })} ${detail.symbol}`
    : `${coin.amount} ${coin.denom}`;

  const proposalGroup = (title, items) => items?.length > 0 && <div className="cosmos-proposal-group">
    <h3>{title}</h3>
    <ul>{items.map((proposal) => <li key={proposal.id}>
      <button type="button" className={selectedId === proposal.id ? 'selected' : ''} aria-expanded={selectedId === proposal.id}
        onClick={() => selectProposal(proposal.id)}>
        <span className="cosmos-proposal-number">#{proposal.id}</span>
        <span className="cosmos-proposal-title">{proposal.title}</span>
        <span className="cosmos-proposal-stage">{stageLabel(proposal.status)}</span>
      </button>
      {selectedId === proposal.id && detailPanel}
    </li>)}</ul>
  </div>;

  const detailPanel = selectedId && <div className="cosmos-proposal-detail" aria-live="polite">
      {!detail && !detailError && <p role="status">Loading proposal #{selectedId}...</p>}
      {detailError && <p role="alert">{detailError}</p>}
      {detail && <>
        <div className="cosmos-proposal-detail-head">
          <div><span>Proposal #{detail.id} · {stageLabel(detail.status)}</span><h3>{detail.title}</h3></div>
          <button type="button" onClick={() => setSelectedId(null)} aria-label="Close proposal details">Close</button>
        </div>
        {detail.summary && <p className="cosmos-proposal-summary">{detail.summary}</p>}
        <div className="cosmos-proposal-facts">
          <div><span>Submitted</span><strong>{dateLabel(detail.submitTime)}</strong></div>
          {detail.depositEndTime && <div><span>Deposit ends</span><strong>{dateLabel(detail.depositEndTime)}</strong></div>}
          {detail.votingStartTime && <div><span>Voting started</span><strong>{dateLabel(detail.votingStartTime)}</strong></div>}
          {detail.votingEndTime && <div><span>Voting ends</span><strong>{dateLabel(detail.votingEndTime)}</strong></div>}
          {detail.proposer && <div><span>Proposer</span><strong>{detail.proposer}</strong></div>}
          {detail.totalDeposit?.length > 0 && <div><span>Total deposit</span><strong>{detail.totalDeposit.map(depositAmount).join(', ')}</strong></div>}
          {detail.voterCount !== null && <div><span>Votes cast</span><strong>{detail.voterCount.toLocaleString('en-GB')}</strong></div>}
          {detail.expedited && <div><span>Priority</span><strong>Expedited</strong></div>}
        </div>
        {detail.failedReason && <p className="cosmos-proposal-failure">Reason: {detail.failedReason}</p>}
        {detail.status !== 'PROPOSAL_STATUS_DEPOSIT_PERIOD' && <div className="cosmos-proposal-voting">
          <h4>Voting tally</h4>
          {tallyTotal > 0 ? voteOptions.map((option) => {
            const share = Number(detail.tally?.[option.key] || 0) / tallyTotal * 100;
            return <div className="cosmos-proposal-vote" key={option.key}>
              <div><span>{option.label}</span><strong>{share.toFixed(2)}% · {voteAmount(detail.tally?.[option.key])}</strong></div>
              <div className="cosmos-proposal-vote-track"><span style={{ width: `${share}%` }} /></div>
            </div>;
          }) : <p>No votes have been tallied yet.</p>}
          {(detail.tallyRules?.quorum || detail.tallyRules?.threshold) && <p className="cosmos-proposal-rules">
            {detail.tallyRules.quorum && `Quorum: ${(Number(detail.tallyRules.quorum) * 100).toFixed(2)}%`}
            {detail.tallyRules.quorum && detail.tallyRules.threshold && ' · '}
            {detail.tallyRules.threshold && `Pass threshold: ${(Number(detail.tallyRules.threshold) * 100).toFixed(2)}%`}
          </p>}
        </div>}
        {detail.status === 'PROPOSAL_STATUS_DEPOSIT_PERIOD' && <p>Voting begins after the required deposit is met.</p>}
        {detail.metadata && <p className="cosmos-proposal-metadata"><strong>Metadata:</strong> {detail.metadata}</p>}
        {detail.messages?.length > 0 && <details className="cosmos-proposal-messages"><summary>On-chain proposal messages</summary><pre>{JSON.stringify(detail.messages, null, 2)}</pre></details>}
      </>}
    </div>;

  return <div id="cosmos-proposal-explorer" className="cosmos-proposal-explorer">
    {listError && <p role="alert">{listError}</p>}
    {!proposals && !listError && <p role="status">Loading proposals...</p>}
    {proposals && <>
      {proposalGroup('Open proposals', proposals.current)}
      {proposalGroup('Recent proposals', proposals.recent)}
      {selectedId && ![...(proposals.current || []), ...(proposals.recent || [])].some((proposal) => proposal.id === selectedId) && detailPanel}
      {!proposals.current?.length && !proposals.recent?.length && <p>No proposals are available from this chain right now.</p>}
    </>}
  </div>;
};

export default ProposalExplorer;
